import Phaser from "phaser";
import { playSound } from "../utils/sound-utils";

export interface NavButtonConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  text: string;
  icon?: string;
  iconFrame?: number | string;
  // Overrides the default square icon size — for non-square art (e.g. the season ticket).
  iconWidth?: number;
  iconHeight?: number;
  labelFontSize?: string;
  style?: "default" | "success" | "orange" | "season";
  // Texture for the "season" style's checkerboard fill — pick premium vs free art.
  checkerTexture?: string;
  // 0..1 progress toward the next season level — renders as a green vertical
  // "liquid fill" overlay on the "season" style's round checkerboard, rising from
  // the bottom of the circle.
  seasonProgress?: number;
  // Shifts the icon and label together, relative to their default position (negative = up).
  contentOffsetY?: number;
  // Renders as a second line below `text`, at labelFontSize * secondaryFontSizeScale.
  secondaryText?: string;
  secondaryFontSizeScale?: number;
  width?: number;
  baseScale?: number;
  round?: boolean;
  onClick: () => void;
}

// ── Layout constants ──────────────────────────────────────────────────────────
const NAV_BTN_DEFAULT_WIDTH = 72;

// Card
const BTN_H = 60;
const BTN_TOP = -22;         // card top relative to container centre
const BTN_RADIUS = 14;

// Dark outer ring (extends beyond fill — creates the elevated "thick border" look)
const OUTER_W = 4;
// Bright inner rim (stroked just inside the fill edge)
const INNER_W = 2;
const INNER_INSET = 2;

// Drop shadow offset
const SHADOW_DX = 2;
const SHADOW_DY = 6;

// Icon overflows above card top so it "pops" like the reference
const ICON_SIZE = 48;
const ICON_Y = -16;          // 6px above BTN_TOP (-22), 18px of icon inside card

// Label near card bottom
const LABEL_Y = 22;

// Vertical shine — top-half gradient highlight
const SHINE_PAD_X = 8;   // horizontal inset from fill edge
const SHINE_PAD_Y = 5;   // gap below card top
const SHINE_HALF = Math.floor(BTN_H * 0.5);
const SHINE_STEPS = 20;
const SHINE_MAX_ALPHA = 0.22;

// Hit area covers icon overflow + a little padding
const HIT_TOP = -50;
const HIT_H = 100;

// Red checkerboard fill (style: "season") — tiles the premium/free checkered texture
// (same art used elsewhere in the season UI) and pans it for the scrolling effect.
// Clipped to the circle via a GeometryMask — but that mask ignores the masked
// object's ancestor Container transform, so unlike every other layer here it can't
// be nested inside the button's own container; it's a standalone Graphics object
// positioned at the button's actual on-screen coordinates instead (see constructor).
const SEASON_CHECKER_TEXTURE_KEY_DEFAULT = "season-checkered-free";
const SEASON_CHECKER_TEXTURE_SIZE = 256;
const SEASON_CHECKER_SCROLL_DURATION = 6000; // ms to pan one full texture width — wraps seamlessly since a texture tiles onto itself every multiple of its own size

// Green XP-progress fill, drawn over the checkerboard for the "season" style.
const SEASON_PROGRESS_FILL_COLOR = 0x22c55e;
const SEASON_PROGRESS_FILL_ALPHA = 0.75;
// ─────────────────────────────────────────────────────────────────────────────

export class NavButton extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Graphics;
  private border: Phaser.GameObjects.Graphics;
  private shine: Phaser.GameObjects.Graphics;
  private iconImage: Phaser.GameObjects.Image | null = null;
  private label: Phaser.GameObjects.Text;
  private secondaryLabel: Phaser.GameObjects.Text | null = null;
  private config: NavButtonConfig;
  private isActive: boolean = false;
  private isPressed: boolean = false;
  private baseScale: number = 1;
  public readonly buttonWidth: number;

  private checkerTile: Phaser.GameObjects.TileSprite | null = null;
  private checkerMaskGraphics: Phaser.GameObjects.Graphics | null = null;
  private checkerTween: Phaser.Tweens.Tween | null = null;
  private progressFill: Phaser.GameObjects.Graphics | null = null;
  private seasonProgress: number = 0;

  constructor(config: NavButtonConfig) {
    super(config.scene, config.x, config.y);
    this.baseScale = config.baseScale ?? 1;

    this.config = {
      style: "default",
      width: NAV_BTN_DEFAULT_WIDTH,
      ...config,
    };

    this.buttonWidth = this.config.width!;
    const contentOffsetY = config.contentOffsetY ?? 0;

    this.background = new Phaser.GameObjects.Graphics(this.scene);
    this.border = new Phaser.GameObjects.Graphics(this.scene);
    this.shine = new Phaser.GameObjects.Graphics(this.scene);

    if (config.icon) {
      this.iconImage = new Phaser.GameObjects.Image(
        this.scene,
        0,
        ICON_Y + contentOffsetY,
        config.icon,
        config.iconFrame,
      );
      this.iconImage.setDisplaySize(config.iconWidth ?? ICON_SIZE, config.iconHeight ?? ICON_SIZE);
      this.iconImage.setOrigin(0.5);
    }

    const labelFontSize = parseInt(config.labelFontSize ?? (config.icon ? "12px" : "22px"), 10);
    // If no icon, vertically centre the text in the card; otherwise pin near bottom
    const labelBaseY = (config.icon ? LABEL_Y : BTN_TOP + BTN_H / 2) + contentOffsetY;

    let mainLabelY = labelBaseY;
    let secondaryLabelY = labelBaseY;
    let secondaryFontSize = labelFontSize;
    if (config.secondaryText) {
      secondaryFontSize = Math.round(labelFontSize * (config.secondaryFontSizeScale ?? 1));
      const mainLineH = labelFontSize * 1.2;
      const secondaryLineH = secondaryFontSize * 1.2;
      mainLabelY = labelBaseY - secondaryLineH / 2;
      secondaryLabelY = labelBaseY + mainLineH / 2;
    }

    this.label = new Phaser.GameObjects.Text(
      this.scene,
      0,
      mainLabelY,
      config.text,
      {
        fontSize: `${labelFontSize}px`,
        fontFamily: "Anton, sans-serif",
        color: "#ffffff",
        align: "center",
        stroke: "#000000",
        strokeThickness: 5,
      },
    );
    this.label.setOrigin(0.5);

    if (config.secondaryText) {
      this.secondaryLabel = new Phaser.GameObjects.Text(
        this.scene,
        0,
        secondaryLabelY,
        config.secondaryText,
        {
          fontSize: `${secondaryFontSize}px`,
          fontFamily: "Anton, sans-serif",
          color: "#ffffff",
          align: "center",
          stroke: "#000000",
          strokeThickness: 5,
        },
      );
      this.secondaryLabel.setOrigin(0.5);
    }

    const layers: Phaser.GameObjects.GameObject[] = [this.background];

    if (this.config.style === "season" && this.config.round) {
      const diameter = this.buttonWidth;
      this.checkerTile = new Phaser.GameObjects.TileSprite(
        this.scene,
        0,
        0,
        diameter,
        diameter,
        this.config.checkerTexture ?? SEASON_CHECKER_TEXTURE_KEY_DEFAULT,
      );
      layers.push(this.checkerTile);

      this.checkerMaskGraphics = this.scene.add.graphics();
      this.checkerMaskGraphics.setPosition(config.x, config.y);
      this.checkerMaskGraphics.fillStyle(0xffffff);
      this.checkerMaskGraphics.fillCircle(0, 0, (diameter / 2) * this.baseScale);
      this.checkerMaskGraphics.setVisible(false);
      this.checkerTile.setMask(this.checkerMaskGraphics.createGeometryMask());

      this.checkerTween = this.scene.tweens.add({
        targets: this.checkerTile,
        tilePositionX: SEASON_CHECKER_TEXTURE_SIZE,
        tilePositionY: SEASON_CHECKER_TEXTURE_SIZE,
        duration: SEASON_CHECKER_SCROLL_DURATION,
        repeat: -1,
        ease: "Linear",
      });

      // Same circle mask as the checker tile above (shares its geometry, so it
      // stays aligned regardless of baseScale).
      this.progressFill = new Phaser.GameObjects.Graphics(this.scene);
      this.progressFill.setMask(this.checkerMaskGraphics.createGeometryMask());
      layers.push(this.progressFill);
      this.seasonProgress = config.seasonProgress ?? 0;
      this.drawProgressFill();
    }

    layers.push(this.border, this.shine);
    if (this.iconImage) layers.push(this.iconImage);
    layers.push(this.label);
    if (this.secondaryLabel) layers.push(this.secondaryLabel);
    this.add(layers);

    this.updateAppearance();
    this.setupInteractions();
    if (this.baseScale !== 1) this.setScale(this.baseScale);

    config.scene.add.existing(this);
  }

  private getColors(): {
    outerBorder: number;
    fill: number;
    innerRim: number;
    labelColor: string;
  } {
    const { style } = this.config;
    const pressed = this.isPressed;
    const active = this.isActive;

    if (style === "success") {
      return {
        outerBorder: 0x031008,
        fill:        pressed ? 0x0d4020 : 0x145e2b,
        innerRim:    pressed ? 0x22c55e : 0x4ade80,
        labelColor:  "#ffffff",
      };
    }
    if (style === "orange") {
      return {
        outerBorder: 0x120400,
        fill:        pressed ? 0x7c2d00 : 0xa33800,
        innerRim:    pressed ? 0xf97316 : 0xfb923c,
        labelColor:  "#ffffff",
      };
    }
    if (style === "season") {
      // Fill is unused (the checkerboard TileSprite replaces it) — kept for type completeness.
      return {
        outerBorder: 0x1a0505,
        fill:        0x7f1d1d,
        innerRim:    active ? 0xfde047 : pressed ? 0xd97706 : 0xf59e0b,
        labelColor:  "#fbbf24",
      };
    }
    if (active) {
      return {
        outerBorder: 0x030810,
        fill:        0x0f3a80,
        innerRim:    0x7fd8f0,
        labelColor:  "#7fd8f0",
      };
    }
    if (pressed) {
      return {
        outerBorder: 0x030508,
        fill:        0x0c1c38,
        innerRim:    0x3a6090,
        labelColor:  "#c8dff0",
      };
    }
    // Default — vivid blue matching the reference
    return {
      outerBorder: 0x060a12,
      fill:        0x1d55a8,
      innerRim:    0x5b9bd5,
      labelColor:  "#ffffff",
    };
  }

  private updateAppearance(): void {
    const w = this.buttonWidth;
    const c = this.getColors();

    this.background.clear();
    this.border.clear();
    this.shine.clear();

    if (this.config.round) {
      const cy = 0;
      const r = w / 2;

      // ① Shadow
      this.background.fillStyle(0x000000, 0.55);
      this.background.fillCircle(SHADOW_DX, cy + SHADOW_DY, r);

      // ② Dark outer ring
      this.background.fillStyle(c.outerBorder, 1);
      this.background.fillCircle(0, cy, r + OUTER_W);

      // ③ Fill — checkerboard tile (masked to the circle) for "season", solid otherwise
      if (this.checkerTile) {
        this.checkerTile.setAlpha(this.isPressed ? 0.7 : 1);
      } else {
        this.background.fillStyle(c.fill, 1);
        this.background.fillCircle(0, cy, r);
      }

      // ④ Inner rim
      this.border.lineStyle(INNER_W, c.innerRim, 1);
      this.border.strokeCircle(0, cy, r - INNER_INSET);

      // ⑤ Shine — top semi-circle gradient strips
      if (!this.isPressed) {
        for (let i = 0; i < SHINE_STEPS; i++) {
          const t = i / SHINE_STEPS;
          const stripY = cy - r + SHINE_PAD_Y + t * (r - SHINE_PAD_Y);
          const dy = stripY - cy;
          const halfW = Math.sqrt(Math.max(0, r * r - dy * dy)) - SHINE_PAD_X;
          if (halfW <= 0) continue;
          this.shine.fillStyle(0xffffff, SHINE_MAX_ALPHA * (1 - t));
          this.shine.fillRect(-halfW, stripY, halfW * 2, r / SHINE_STEPS + 1);
        }
      }
    } else {
      // ① Drop shadow — offset rect behind the card
      this.background.fillStyle(0x000000, 0.55);
      this.background.fillRoundedRect(
        -w / 2 + SHADOW_DX,
        BTN_TOP + SHADOW_DY,
        w,
        BTN_H,
        BTN_RADIUS,
      );

      // ② Dark outer ring — slightly larger than the fill, creates the thick dark border
      this.background.fillStyle(c.outerBorder, 1);
      this.background.fillRoundedRect(
        -w / 2 - OUTER_W,
        BTN_TOP - OUTER_W,
        w + OUTER_W * 2,
        BTN_H + OUTER_W * 2,
        BTN_RADIUS + OUTER_W,
      );

      // ③ Main vivid fill
      this.background.fillStyle(c.fill, 1);
      this.background.fillRoundedRect(-w / 2, BTN_TOP, w, BTN_H, BTN_RADIUS);

      // ④ Bright inner rim stroke — gives the "light outline inside dark border" look
      this.border.lineStyle(INNER_W, c.innerRim, 1);
      this.border.strokeRoundedRect(
        -w / 2 + INNER_INSET,
        BTN_TOP + INNER_INSET,
        w - INNER_INSET * 2,
        BTN_H - INNER_INSET * 2,
        BTN_RADIUS - INNER_INSET,
      );

      // ⑤ Vertical shine — gradient strips with arc-clipped rounded top corners
      if (!this.isPressed) {
        const shineX = -w / 2 + SHINE_PAD_X;
        const shineW = w - SHINE_PAD_X * 2;
        const shineTopY = BTN_TOP + SHINE_PAD_Y;
        const r = BTN_RADIUS / 2;
        for (let i = 0; i < SHINE_STEPS; i++) {
          const stripY = shineTopY + (i / SHINE_STEPS) * SHINE_HALF;
          const stripH = SHINE_HALF / SHINE_STEPS + 1;
          const dy = stripY - shineTopY;
          const ci = dy < r ? r - Math.sqrt(r * r - (r - dy) * (r - dy)) : 0;
          this.shine.fillStyle(0xffffff, SHINE_MAX_ALPHA * (1 - i / SHINE_STEPS));
          this.shine.fillRect(shineX + ci, stripY, shineW - ci * 2, stripH);
        }
      }
    }

    this.label.setColor(c.labelColor);
  }

  private setupInteractions(): void {
    const w = this.buttonWidth;

    if (this.config.round) {
      this.setInteractive(
        new Phaser.Geom.Circle(0, 0, w / 2 + OUTER_W),
        Phaser.Geom.Circle.Contains,
      );
    } else {
      this.setInteractive(
        new Phaser.Geom.Rectangle(-w / 2, HIT_TOP, w, HIT_H),
        Phaser.Geom.Rectangle.Contains,
      );
    }

    this.on("pointerover", () => {
      if (this.scene?.input) this.scene.input.setDefaultCursor("pointer");
    });

    this.on("pointerout", () => {
      if (this.scene?.input) this.scene.input.setDefaultCursor("default");
      this.isPressed = false;
      this.updateAppearance();
      if (this.scene?.tweens) {
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.add({
          targets: this,
          scaleX: this.baseScale,
          scaleY: this.baseScale,
          duration: 120,
          ease: "Back.Out",
        });
      }
    });

    this.on("pointerdown", () => {
      this.isPressed = true;
      this.updateAppearance();
      if (this.scene?.tweens) {
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.add({
          targets: this,
          scaleX: this.baseScale * 0.92,
          scaleY: this.baseScale * 0.92,
          duration: 80,
          ease: "Power2.In",
        });
      }
    });

    this.on("pointerup", () => {
      if (this.isPressed) {
        playSound(this.scene, "menu-click-sound");
        this.config.onClick();
      }
      this.isPressed = false;
      this.updateAppearance();
      if (this.scene?.tweens) {
        this.scene.tweens.killTweensOf(this);
        this.scene.tweens.add({
          targets: this,
          scaleX: this.baseScale,
          scaleY: this.baseScale,
          duration: 150,
          ease: "Back.Out",
        });
      }
    });
  }

  private drawProgressFill(): void {
    if (!this.progressFill) return;
    const r = this.buttonWidth / 2;
    this.progressFill.clear();
    if (this.seasonProgress <= 0) return;
    // Rises from the bottom of the circle; the mask clips it to the circular shape.
    const fillH = this.seasonProgress * r * 2;
    this.progressFill.fillStyle(SEASON_PROGRESS_FILL_COLOR, SEASON_PROGRESS_FILL_ALPHA);
    this.progressFill.fillRect(-r, r - fillH, r * 2, fillH);
  }

  public setSeasonProgress(progress: number): void {
    this.seasonProgress = Phaser.Math.Clamp(progress, 0, 1);
    this.drawProgressFill();
  }

  public setCheckerTexture(key: string): void {
    if (!this.checkerTile || this.config.checkerTexture === key) return;
    this.config.checkerTexture = key;
    this.checkerTile.setTexture(key);
  }

  public setTabActive(active: boolean): void {
    this.isActive = active;
    this.updateAppearance();
  }

  public setStyle(style: "default" | "success" | "orange"): void {
    this.config.style = style;
    this.updateAppearance();
  }

  public setText(text: string): void {
    this.label.setText(text);
  }

  public setSecondaryText(text: string): void {
    this.secondaryLabel?.setText(text);
  }

  destroy(fromScene?: boolean): void {
    this.checkerTween?.stop();
    this.checkerTween = null;
    // Not a child of `this` (see constructor) — Container.destroy() won't reach it.
    this.checkerMaskGraphics?.destroy();
    this.checkerMaskGraphics = null;
    super.destroy(fromScene);
  }
}
